import { FileDownload } from '@mui/icons-material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import { Box, MenuItem, Menu } from '@mui/material';
import { useTranslate } from 'ra-core';
import { useListContext, Button } from 'react-admin';
import { useCallback, useState } from 'react';

import { employeesDataProvider } from '@/dataProvider';

type EmployeeActionsType = 'import_csv' | 'export_employees';

interface IEmployeeActionsDropdownProps {
  handleOpenImportCSVModal: () => void;
}

function EmployeeActionsDropdown({
  handleOpenImportCSVModal,
}: IEmployeeActionsDropdownProps): JSX.Element {
  const t = useTranslate();
  const { filterValues, sort } = useListContext();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isButtonEnabled, setIsButtonEnabled] = useState(true);
  const isMenuOpen = Boolean(anchorEl);

  const exportEmployees = async (): Promise<void> => {
    const response = await employeesDataProvider.exportEmployees({
      filter: {
        companyId: filterValues?.companies,
        cohortId: filterValues?.cohorts,
        status: filterValues?.status,
        full_name: filterValues?.full_name,
      },
      sort,
    });

    if (response?.data) {
      const contentDisposition = response.headers.get('Content-Disposition');
      const fileNameMatch = /filename="([^"]+)"/g.exec(contentDisposition);

      const url = URL.createObjectURL(response.data);

      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameMatch ? fileNameMatch[1] : 'export.xls';
      link.style.display = 'none';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClick = useCallback(
    async (type: EmployeeActionsType) => {
      setIsButtonEnabled(false);

      handleMenuClose();

      if (type === 'import_csv') {
        handleOpenImportCSVModal();
      } else if (type === 'export_employees') {
        await exportEmployees();
      }

      setIsButtonEnabled(true);
    },
    [handleMenuClose],
  );

  return (
    <Box>
      <div>
        <Button
          id="basic-button"
          startIcon={<AccountCircleOutlinedIcon />}
          aria-controls={isMenuOpen ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ? 'true' : undefined}
          onClick={handleMenuOpen}
          label="resources.employees.actions_dropdown"
          disabled={!isButtonEnabled}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <MenuItem onClick={() => handleClick('import_csv')}>
            <AddCircleOutlineIcon fontSize="small" />
            {' '}
            <div>{t('resources.employees.import_csv')}</div>
          </MenuItem>
          <MenuItem onClick={() => handleClick('export_employees')}>
            <FileDownload fontSize="small" />
            {' '}
            <div>{t('resources.employees.export_employees')}</div>
          </MenuItem>
        </Menu>
      </div>
    </Box>
  );
}

export default EmployeeActionsDropdown;
